import React, { useState, useEffect, useMemo } from 'react'
import { Clock, Calendar } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./components/ui/table"

interface Period {
  name: string
  start: Date
  end: Date
  duration: string
  isPassing?: boolean
}

interface Schedule {
  name: string
  periods: Period[]
}

// Format time to 12-hour format
function formatTimeToLA(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

// Get today's schedule based on day of week and special dates
function getTodaySchedule(): Schedule {
  const now = new Date()
  const dayOfWeek = now.getDay() // 0 = Sunday, 1 = Monday, etc.
  const dateKey = now.toISOString().slice(0, 10)
  
  // Special minimum day dates
  const minimumDays = ['2024-01-15', '2024-02-19', '2024-03-15']
  
  // Minimum Day Schedule
  if (minimumDays.includes(dateKey)) {
    return {
      name: 'Minimum Day Schedule',
      periods: [
        { name: 'Period 1', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 8, 30), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 15), duration: '45m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 15), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 20), duration: '5m', isPassing: true },
        { name: 'Period 2', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 20), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 5), duration: '45m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 5), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 10), duration: '5m', isPassing: true },
        { name: 'Period 3', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 10), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 55), duration: '45m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 55), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 0), duration: '5m', isPassing: true },
        { name: 'Period 4', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 0), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 45), duration: '45m' },
        { name: 'Lunch', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 45), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 15), duration: '30m' },
        { name: 'Period 5', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 15), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 0), duration: '45m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 0), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 5), duration: '5m', isPassing: true },
        { name: 'Period 6', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 5), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 50), duration: '45m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 50), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 55), duration: '5m', isPassing: true },
        { name: 'Period 7', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 55), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 14, 40), duration: '45m' },
      ]
    }
  }
  
  // Default Regular Day Schedule (Monday-Friday)
  if (dayOfWeek >= 1 && dayOfWeek <= 5) {
    return {
      name: 'Regular Day Schedule 2025-26 (6th & 7th Grades)',
      periods: [
        { name: 'Warning Bell', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 7, 50), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 7, 50), duration: '0m' },
        { name: 'Period 1', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 8, 0), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 8, 58), duration: '58m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 8, 58), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 4), duration: '6m', isPassing: true },
        { name: 'Period 2', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 4), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 57), duration: '53m' },
        { name: 'Nutrition', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 57), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 12), duration: '15m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 12), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 18), duration: '6m', isPassing: true },
        { name: 'Period 3', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 18), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 11), duration: '53m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 11), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 17), duration: '6m', isPassing: true },
        { name: 'Period 4', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 17), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 10), duration: '53m' },
        { name: 'Lunch', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 16), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 40), duration: '24m' },
        { name: 'Advisory', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 40), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 10), duration: '30m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 10), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 16), duration: '6m', isPassing: true },
        { name: 'Period 5', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 16), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 14, 9), duration: '53m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 14, 9), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 14, 15), duration: '6m', isPassing: true },
        { name: 'Period 6', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 14, 15), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 15, 8), duration: '53m' },
      ]
    }
  }
  
  // Friday - PD Day
  if (dayOfWeek === 5) {
    return {
      name: 'PD Day Schedule (Fri)',
      periods: [
        { name: 'Period 1', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 8, 30), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 15), duration: '45m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 15), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 20), duration: '5m', isPassing: true },
        { name: 'Period 2', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 20), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 5), duration: '45m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 5), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 10), duration: '5m', isPassing: true },
        { name: 'Period 3', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 10), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 55), duration: '45m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 10, 55), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 0), duration: '5m', isPassing: true },
        { name: 'Period 4', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 0), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 45), duration: '45m' },
        { name: 'Lunch', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 45), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 15), duration: '30m' },
        { name: 'Period 5', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 15), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 0), duration: '45m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 0), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 5), duration: '5m', isPassing: true },
        { name: 'Period 6', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 5), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 50), duration: '45m' },
        { name: 'Passing', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 50), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 55), duration: '5m', isPassing: true },
        { name: 'Period 7', start: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 13, 55), end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 14, 40), duration: '45m' },
      ]
    }
  }
  
  // Weekend
  return {
    name: 'No School (Weekend)',
    periods: []
  }
}

function getCurrentPeriodInfo(periods: Period[], currentTime: Date) {
  let currentPeriodIndex = -1
  let timeRemaining = 0
  let progressPercent = 0
  let currentPeriod: Period | null = null
  let isPassingPeriod = false
  
  for (let i = 0; i < periods.length; i++) {
    const period = periods[i]
    if (currentTime >= period.start && currentTime < period.end) {
      currentPeriodIndex = i
      currentPeriod = period
      isPassingPeriod = period.isPassing || false
      
      const totalDuration = period.end.getTime() - period.start.getTime()
      const elapsed = currentTime.getTime() - period.start.getTime()
      const remaining = totalDuration - elapsed
      
      timeRemaining = Math.max(0, remaining)
      progressPercent = Math.min(100, (elapsed / totalDuration) * 100)
      break
    }
  }
  
  return {
    currentPeriodIndex,
    timeRemaining,
    progressPercent,
    currentPeriod,
    isPassingPeriod
  }
}

function formatTimeRemaining(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  
  if (minutes > 0) {
    return `${minutes}m ${seconds}s` 
  }
  return `${seconds}s` 
}

function StatusDisplay({ currentPeriod, timeRemaining, progressPercent, isPassingPeriod }: {
  currentPeriod: Period | null
  timeRemaining: number
  progressPercent: number
  isPassingPeriod: boolean
}) {
  const [prevProgress, setPrevProgress] = useState(0)
  
  useEffect(() => {
    setPrevProgress(progressPercent)
  }, [progressPercent])

  if (!currentPeriod) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center animate-fadeIn">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 animate-pulse">
            <Clock className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-lg">No class in session</p>
        </div>
      </div>
    )
  }
  
  const getProgressColor = () => {
    if (progressPercent > 80) return 'bg-green-500'
    if (progressPercent > 50) return 'bg-blue-500'
    return 'bg-blue-500'
  }

  const isProgressIncreasing = progressPercent > prevProgress
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-slideUp">
      <div className="flex items-center justify-between mb-4">
        <div className="flex-1">
          <h2 className={`text-2xl font-bold text-gray-900 transition-all duration-300 ${
            isPassingPeriod ? 'text-orange-600' : ''
          }`}>
            <span className={`inline-block transition-all duration-300 ${
              isProgressIncreasing ? 'animate-pulse' : ''
            }`}>
              {isPassingPeriod ? '🏃 Passing Period' : currentPeriod.name}
            </span>
          </h2>
          <p className="text-gray-600 mt-1">
            {formatTimeToLA(currentPeriod.start)} - {formatTimeToLA(currentPeriod.end)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500 mb-1">Time Remaining</p>
          <p className={`text-3xl font-bold transition-all duration-200 ${
            timeRemaining < 60000 ? 'text-red-600 animate-pulse' : 'text-gray-900'
          }`}>
            {formatTimeRemaining(timeRemaining)}
          </p>
        </div>
      </div>
      
      <div className="mb-2">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progress</span>
          <span className="font-medium">{Math.round(progressPercent)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden relative">
          <div 
            className={`h-full ${getProgressColor()} transition-all duration-1000 ease-out relative overflow-hidden`}
            style={{ width: `${progressPercent}%` }}
          >
            <div className="absolute inset-0 bg-white opacity-20 animate-shimmer" />
          </div>
        </div>
      </div>
    </div>
  )
}

function ScheduleTable({ periods, currentPeriodIndex }: {
  periods: Period[]
  currentPeriodIndex: number
}) {
  // Filter out passing periods from display
  const classPeriods = periods.filter(period => !period.isPassing)

  // Find the index in the filtered list that corresponds to the current period
  const currentClassIndex = currentPeriodIndex >= 0 
    ? classPeriods.findIndex(p => p.start.getTime() === periods[currentPeriodIndex]?.start.getTime())
    : -1

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-slideUp" style={{ animationDelay: '0.2s' }}>
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-blue-600" />
          Today's Schedule
        </h3>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Period</TableHead>
            <TableHead>Start Time</TableHead>
            <TableHead>End Time</TableHead>
            <TableHead>Duration</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {classPeriods.map((period, index) => (
            <TableRow 
              key={index}
              className={`transition-all duration-300 hover:bg-gray-50 ${
                index === currentClassIndex 
                  ? 'bg-blue-50 border-l-4 border-blue-500 animate-pulse' 
                  : ''
              }`}
              aria-current={index === currentClassIndex ? 'step' : undefined}
              style={{ 
                animationDelay: `${0.1 + index * 0.05}s`,
                animation: 'fadeInUp 0.5s ease-out forwards'
              }}
            >
              <TableCell className="font-medium">
                <span className={`inline-flex items-center ${
                  index === currentClassIndex ? 'text-blue-700 font-bold' : ''
                }`}>
                  {index === currentClassIndex && (
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse" />
                  )}
                  {period.name}
                </span>
              </TableCell>
              <TableCell className={index === currentClassIndex ? 'text-blue-700 font-medium' : ''}>
                {formatTimeToLA(period.start)}
              </TableCell>
              <TableCell className={index === currentClassIndex ? 'text-blue-700 font-medium' : ''}>
                {formatTimeToLA(period.end)}
              </TableCell>
              <TableCell className={index === currentClassIndex ? 'text-blue-700 font-medium' : ''}>
                {period.duration}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default function App() {
  const [currentTime, setCurrentTime] = useState(new Date())
  const [schedule, setSchedule] = useState<Schedule>(getTodaySchedule())
  const [isLoading, setIsLoading] = useState(true)
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    
    // Initial loading animation
    setTimeout(() => setIsLoading(false), 500)
    
    return () => clearInterval(timer)
  }, [])
  
  useEffect(() => {
    // Update schedule at midnight
    const checkSchedule = () => {
      const newSchedule = getTodaySchedule()
      setSchedule(newSchedule)
    }
    
    const now = new Date()
    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)
    
    const msUntilMidnight = tomorrow.getTime() - now.getTime()
    
    const midnightTimer = setTimeout(() => {
      checkSchedule()
      // Set up daily check
      const dailyTimer = setInterval(checkSchedule, 24 * 60 * 60 * 1000)
      return () => clearInterval(dailyTimer)
    }, msUntilMidnight)
    
    return () => clearTimeout(midnightTimer)
  }, [])
  
  const periodInfo = useMemo(() => 
    getCurrentPeriodInfo(schedule.periods, currentTime), 
    [schedule.periods, currentTime]
  )
  
  const { currentPeriodIndex, timeRemaining, progressPercent, currentPeriod, isPassingPeriod } = periodInfo
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <Clock className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 animate-pulse">BellTrack</h1>
          <p className="text-gray-600 mt-2">Loading schedule...</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeInUp {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0; 
            transform: translateY(30px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out;
        }
        
        .animate-slideUp {
          animation: slideUp 0.6s ease-out;
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
      
      <div className="max-w-4xl mx-auto px-4">
        <header className="mb-8 animate-fadeIn">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-600 rounded-lg p-3 shadow-lg transform transition-transform duration-200 hover:scale-110">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              BellTrack
            </h1>
          </div>
          <p className="text-gray-600 flex items-center gap-2 ml-12">
            <Calendar className="w-4 h-4" />
            {schedule.name}
          </p>
        </header>
        
        {schedule.periods.length > 0 ? (
          <>
            <StatusDisplay 
              currentPeriod={currentPeriod}
              timeRemaining={timeRemaining}
              progressPercent={progressPercent}
              isPassingPeriod={isPassingPeriod}
            />
            
            <div className="mt-8">
              <ScheduleTable 
                periods={schedule.periods}
                currentPeriodIndex={currentPeriodIndex}
              />
            </div>
          </>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center animate-fadeIn">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Calendar className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-gray-500 text-lg">No school today - enjoy your weekend!</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
